import type { OrderListResultData } from '@frontend/api';
import { AvatarWithName } from '@frontend/legacy-components';

import {
  DateCellContent,
  DateTimeCellContent,
  EmptyCellContent,
  PersonCellContent,
} from 'components/listTablePage';

import { OrderNumberCellContent, PriceCellContent } from '../components';
import type { OrderTableColumnConfig, OrderTableColumnId } from '../types';

export const ORDER_TABLE_COLUMN_ID_TO_CONFIG_MAP = {
  orderNumber: {
    columnId: 'orderNumber',
    title: 'Order #',
    sortKey: 'number',
    render: function renderOrderNumber(rowData: OrderListResultData) {
      return <OrderNumberCellContent rowData={rowData} />;
    },
    width: 16,
  },
  account: {
    columnId: 'account',
    title: 'Account',
    sortKey: 'account',
    render: function renderAccount(rowData: OrderListResultData) {
      return <>{rowData.contact?.account?.name ?? <EmptyCellContent />}</>;
    },
    width: 100,
  },
  contact: {
    columnId: 'contact',
    title: 'Contact',
    sortKey: 'contact',
    render: function renderContact(rowData: OrderListResultData) {
      const { contact } = rowData;
      return contact ? (
        <PersonCellContent
          firstName={contact.firstName}
          lastName={contact.lastName}
        />
      ) : (
        <EmptyCellContent />
      );
    },
    width: 150,
  },
  salesperson: {
    columnId: 'salesperson',
    title: 'Salesperson',
    sortKey: 'salesperson',
    render: function renderSalesperson(rowData: OrderListResultData) {
      const { salespersonDetails } = rowData;
      return (
        <AvatarWithName
          name={salespersonDetails?.fullName}
          backgroundColor={salespersonDetails?.avatarColor}
        />
      );
    },

    width: 150,
  },
  price: {
    columnId: 'price',
    title: 'Price',
    sortKey: 'price',
    render: function renderPrice(rowData: OrderListResultData) {
      return <PriceCellContent rowData={rowData} />;
    },
    width: 100,
  },
  orderedOn: {
    columnId: 'orderedOn',
    title: 'Ordered On',
    sortKey: 'ordered_on',
    render: function renderOrderedOn(rowData: OrderListResultData) {
      return <DateTimeCellContent dateTime={rowData.created} />;
    },
    width: 115,
  },
  shipsOn: {
    columnId: 'shipsOn',
    title: 'Ships On',
    sortKey: 'ships_on',
    render: function renderShipsOn(rowData: OrderListResultData) {
      return <DateCellContent date={rowData.shipsOn} />;
    },
    width: 110,
  },
  deliverBy: {
    columnId: 'deliverBy',
    title: 'Deliver By',
    sortKey: 'deliver_by',
    render: function renderDeliverBy(rowData: OrderListResultData) {
      return <DateTimeCellContent dateTime={rowData.deliverBy} />;
    },
    width: 110,
  },
  payment: {
    columnId: 'payment',
    title: 'Payment',
    sortKey: 'payment',
    render: function renderPayment(rowData: OrderListResultData) {
      return <>{rowData.paymentStatus ?? <EmptyCellContent />}</>;
    },
    width: 100,
  },
  po: {
    columnId: 'po',
    title: 'PO',
    sortKey: 'po_number',
    render: function renderPo(rowData: OrderListResultData) {
      return <>{rowData.purchaseToken ?? <EmptyCellContent />}</>;
    },
    width: 100,
  },
} as const satisfies Record<OrderTableColumnId, OrderTableColumnConfig>;
